import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { OfflineError } from '@zibanu/client';
import { useDeps } from './deps';
import { useBooks, useMe } from './session';

/**
 * Offline (spec 3A §6.4): by the phone's own signal, or because a read on
 * screen just failed for want of a connection. null when online; otherwise
 * when the oldest thing on screen was fetched, or 0 when nothing on screen
 * was saved.
 */
export function useOfflineSince(): number | null {
  const { queryClient } = useDeps();
  const orgId = useBooks().current?.orgId;
  const snapshot = useCallback((): number | null => {
    const onScreen = orgId ? queryClient.getQueryCache().findAll({ queryKey: [orgId], type: 'active' }) : [];
    const offline = !onlineManager.isOnline() || onScreen.some((q) => q.state.error instanceof OfflineError);
    if (!offline) return null;
    const seen = onScreen.map((q) => q.state.dataUpdatedAt).filter((t) => t > 0);
    return seen.length > 0 ? Math.min(...seen) : 0;
  }, [orgId, queryClient]);
  const subscribe = useCallback(
    (changed: () => void) => {
      const fromCache = queryClient.getQueryCache().subscribe(() => changed());
      const fromNetwork = onlineManager.subscribe(() => changed());
      return () => {
        fromCache();
        fromNetwork();
      };
    },
    [queryClient],
  );
  return useSyncExternalStore(subscribe, snapshot);
}

/**
 * Books this person is no longer on leave nothing behind (Review Focus 5):
 * whenever /me is read, anything cached for other books goes, so a copy
 * saved just before access ended doesn't come back on the next start.
 */
export function useForgetOtherBooks(): void {
  const { queryClient, outbox } = useDeps();
  const ids = useMe().data?.memberships.map((m) => m.orgId).join(',');
  useEffect(() => {
    if (ids === undefined) return;
    const keep = new Set(ids.split(','));
    queryClient.removeQueries({
      predicate: (q) => {
        const [first] = q.queryKey;
        return typeof first === 'string' && first !== 'me' && !keep.has(first);
      },
    });
    // Their captures waiting, failed or sent on this phone go too.
    void (async () => {
      const left = [...(await outbox.queued()), ...(await outbox.failed()), ...(await outbox.sent())].map((c) => c.orgId);
      for (const orgId of new Set(left)) if (!keep.has(orgId)) await outbox.clearOrg(orgId).catch(() => undefined);
    })().catch(() => undefined);
  }, [ids, queryClient, outbox]);
}
